import { useQuery } from "@tanstack/react-query";
import { formatBRL } from "@yatta/domain";
import { env } from "../lib/env";
import { supabase } from "../lib/supabase";
import { useDataQueryScope } from "../hooks/useDataQueryScope";
import {
  fakeCustomerDashboardMetrics,
  fakeCustomerToListRow,
  listFakeCustomerRecords,
  useFakeCustomerStoreVersion,
} from "./fake-customer-store";
import {
  commissions as mockCommissions,
  contracts as mockContracts,
  integrations as mockIntegrations,
  invoices as mockInvoices,
  sales as mockSales,
  telemedicine as mockTelemedicine,
} from "./mock-data";

export interface CustomerRow {
  id: string;
  name: string;
  holder: string;
  phone: string;
  funeral: string;
  telemedicine: string;
  seller: string;
  balance: string;
}
export interface SaleRow {
  id: string;
  createdAt: string;
  customer: string;
  offer: string;
  seller: string;
  source: string;
  status: string;
  amount: string;
}
export interface ContractRow {
  id?: string;
  number: string;
  customer: string;
  type: string;
  status: string;
  version: string;
  updatedAt: string;
  internalStatus?: "DRAFT" | "IN_REVIEW" | "INTERNALLY_APPROVED" | "REJECTED";
  revision?: number;
  reviewNotes?: string;
}
export interface InvoiceRow {
  number: string;
  customer: string;
  products: string;
  dueDate: string;
  total: string;
  status: string;
  method: string;
}
export interface CommissionRow {
  id: string;
  seller: string;
  product: string;
  sale: string;
  amount: string;
  status: string;
}
export interface TelemedicineRow {
  id: string;
  person: string;
  family: string;
  externalId: string;
  status: string;
  firstAccess: string;
}
export interface IntegrationRow {
  provider: string;
  mode: string;
  status: string;
  lastEvent: string;
}

type ViewValue = string | number | boolean | null;

async function queryView<T extends Record<string, ViewValue>>(view: string): Promise<T[]> {
  const { data, error } = await supabase.from(view).select("*");
  if (error) throw error;
  return (data ?? []) as T[];
}

export function useCustomers() {
  const queryScope = useDataQueryScope();
  const demoStoreVersion = useFakeCustomerStoreVersion();
  return useQuery({
    queryKey: queryScope.queryKey(["customers", env.useMocks ? demoStoreVersion : "persistent"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<CustomerRow[]> => {
      if (env.useMocks) return listFakeCustomerRecords().map(fakeCustomerToListRow);
      const rows = await queryView<Record<string, ViewValue>>("app_customer_directory");
      return rows.map((row) => ({
        id: String(row.id),
        name: String(row.name ?? "Família"),
        holder: String(row.holder ?? "—"),
        phone: String(row.phone ?? "—"),
        funeral: String(row.funeral ?? "NOT_CONTRACTED"),
        telemedicine: String(row.telemedicine ?? "NOT_CONTRACTED"),
        seller: String(row.seller ?? "Sem vendedor"),
        balance: String(row.balance ?? "Em dia"),
      }));
    },
  });
}

export function useCustomer(id?: string) {
  const all = useCustomers();
  return {
    ...all,
    data: id ? all.data?.find((row) => row.id === id) : undefined,
  };
}

export function useSales() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["sales"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<SaleRow[]> => {
      if (env.useMocks) return mockSales;
      const rows = await queryView<Record<string, ViewValue>>("app_sales_directory");
      return rows.map((row) => ({
        id: String(row.id),
        createdAt: new Date(String(row.created_at)).toLocaleString("pt-BR"),
        customer: String(row.customer ?? "Cliente"),
        offer: String(row.offer ?? "Oferta"),
        seller: String(row.seller ?? "Sem vendedor"),
        source: String(row.source ?? "MANUAL"),
        status: String(row.status ?? "DRAFT"),
        amount: formatBRL(BigInt(String(row.total_first_cycle_cents ?? 0))),
      }));
    },
  });
}

export function useContracts() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["contracts"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<ContractRow[]> => {
      if (env.useMocks) return mockContracts;
      const rows = await queryView<Record<string, ViewValue>>("app_contract_directory");
      const { data: internalRows, error: internalError } = await supabase
        .from("contract_internal_states")
        .select("contract_id, status, revision, review_notes");
      if (internalError) throw internalError;
      const internal = new Map((internalRows ?? []).map((row) => [row.contract_id, row]));
      return rows.map((row) => {
        const state = internal.get(String(row.id));
        return {
          id: String(row.id),
          number: String(row.number),
          customer: String(row.customer ?? "Cliente"),
          type: String(row.type ?? "CONTRACT"),
          status: String(row.status ?? "DRAFT"),
          version: String(row.version ?? "v1"),
          updatedAt: new Date(String(row.updated_at)).toLocaleDateString("pt-BR"),
          ...(state
            ? {
                internalStatus: String(state.status) as ContractRow["internalStatus"],
                revision: Number(state.revision),
                reviewNotes: String(state.review_notes ?? "") || undefined,
              }
            : {}),
        };
      });
    },
  });
}

export function useInvoices() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["invoices"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<InvoiceRow[]> => {
      if (env.useMocks) return mockInvoices;
      const rows = await queryView<Record<string, ViewValue>>("app_invoice_directory");
      return rows.map((row) => ({
        number: String(row.number),
        customer: String(row.customer ?? "Cliente"),
        products: String(row.products ?? "—"),
        dueDate: new Date(String(row.due_date)).toLocaleDateString("pt-BR"),
        total: formatBRL(BigInt(String(row.total_cents ?? 0))),
        status: String(row.status ?? "OPEN"),
        method: String(row.method ?? "—"),
      }));
    },
  });
}

export function useCommissions() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["commissions"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<CommissionRow[]> => {
      if (env.useMocks) {
        return mockCommissions.map((entry, index) => ({ id: String(index), ...entry }));
      }
      const rows = await queryView<Record<string, ViewValue>>("app_commission_directory");
      return rows.map((row) => ({
        id: String(row.id),
        seller: String(row.seller ?? "Vendedor"),
        product: String(row.product ?? "—"),
        sale: String(row.sale ?? "—"),
        amount: formatBRL(BigInt(String(row.amount_cents ?? 0))),
        status: String(row.status ?? "ESTIMATED"),
      }));
    },
  });
}

export function useTelemedicine() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["telemedicine"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<TelemedicineRow[]> => {
      if (env.useMocks) {
        return mockTelemedicine.map((entry) => ({ id: entry.externalId, ...entry }));
      }
      const rows = await queryView<Record<string, ViewValue>>("app_telemedicine_directory");
      return rows.map((row) => ({
        id: String(row.id),
        person: String(row.person ?? "Beneficiário"),
        family: String(row.family ?? "Família"),
        externalId: String(row.external_id ?? "—"),
        status: String(row.status ?? "PENDING"),
        firstAccess: String(row.first_access ?? "Pendente"),
      }));
    },
  });
}

export function useIntegrations() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["integrations"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<IntegrationRow[]> => {
      if (env.useMocks) return mockIntegrations;
      const rows = await queryView<Record<string, ViewValue>>("app_integration_directory");
      return rows.map((row) => ({
        provider: String(row.provider),
        mode: String(row.mode),
        status: String(row.status),
        lastEvent: row.last_health_check_at
          ? new Date(String(row.last_health_check_at)).toLocaleString("pt-BR")
          : "Sem evento",
      }));
    },
  });
}

export interface ProductRow {
  id: string;
  code: string;
  name: string;
  productType: string;
  status: string;
  activeVersion: number | null;
}

export function useProducts() {
  const queryScope = useDataQueryScope();
  return useQuery({
    queryKey: queryScope.queryKey(["products"]),
    enabled: queryScope.enabled,
    queryFn: async (): Promise<ProductRow[]> => {
      if (env.useMocks) {
        return [
          {
            id: "funeral",
            code: "FUNERAL",
            name: "Plano Funerário",
            productType: "FUNERAL",
            status: "ACTIVE",
            activeVersion: 1,
          },
          {
            id: "telemedicine",
            code: "TELEMEDICINE",
            name: "Telemedicina MobiMedical",
            productType: "TELEMEDICINE",
            status: "ACTIVE",
            activeVersion: 1,
          },
        ];
      }
      const rows = await queryView<Record<string, ViewValue>>("app_product_directory");
      return rows.map((row) => ({
        id: String(row.id),
        code: String(row.code),
        name: String(row.name),
        productType: String(row.product_type),
        status: String(row.status),
        activeVersion: row.active_version == null ? null : Number(row.active_version),
      }));
    },
  });
}

export function useDashboardData() {
  const sales = useSales();
  const customers = useCustomers();
  const invoices = useInvoices();
  const commissions = useCommissions();

  if (env.useMocks) {
    return {
      metrics: fakeCustomerDashboardMetrics(),
      sales: sales.data ?? [],
      loading: sales.isLoading || customers.isLoading,
      error: sales.error ?? customers.error,
    };
  }

  const activeFuneral = (customers.data ?? []).filter((row) => row.funeral === "ACTIVE").length;
  const activeTelemedicine = (customers.data ?? []).filter(
    (row) => row.telemedicine === "ACTIVE",
  ).length;
  const overdue = (invoices.data ?? []).filter((row) => row.status === "OVERDUE").length;

  return {
    metrics: [
      { label: "Famílias", value: String(customers.data?.length ?? 0), delta: "Base ativa" },
      { label: "Planos funerários", value: String(activeFuneral), delta: "Ativos" },
      { label: "Telemedicina", value: String(activeTelemedicine), delta: "Vidas ativas" },
      { label: "Vendas", value: String(sales.data?.length ?? 0), delta: "Pedidos" },
      { label: "Faturas vencidas", value: String(overdue), delta: "Exigem ação" },
      { label: "Comissões", value: String(commissions.data?.length ?? 0), delta: "Lançamentos" },
    ],
    sales: sales.data ?? [],
    loading: sales.isLoading || customers.isLoading || invoices.isLoading,
    error: sales.error ?? customers.error ?? invoices.error,
  };
}
